'use client';

import { useApiService } from '@shared/api-utils';
import { useSession } from '@shared/auth';
import { ROUTES } from '@shared/utils';
import { useTranslations } from 'next-intl';
import { redirect } from 'next/navigation';
import { InnerPageContainer } from '../InnerPageContainer/InnerPageContainer';
import { NoResults } from '../NoResults/NoResults';
import { MultipleOffer, OfferLoading, SingleOffer } from './components';
import { usePrivateOfferBundleData } from './hooks/usePrivateOfferBundleData';
import { usePublicOfferBundleData } from './hooks/usePublicOfferBundleData';
import type { OfferBundleDetails } from './types';

type OfferProps = {
  code?: string;
  iterationNr?: number;
  className?: string;
};

function getDataTrackingAttributes(data: OfferBundleDetails | null) {
  const type = data?.offers && data.offers.length > 1 ? 'multi-reward' : 'single-reward';

  return data
    ? {
        'data-track-type': type,
        'data-track-expiry-date': data.expirationDate,
        'data-track-iteration': data.iterationNr,
        'data-track-status': data.bundleStatus,
        'data-track-step': data.step?.action,
        'data-track-title': data.title,
        'data-track-code': data.bundleCode,
        'data-track-valid-date': data.validFromDate,
        'data-track-step-amount': data.step?.initialAmount?.amount,
      }
    : {};
}

function PrivateOffer({ code, iterationNr }: Pick<OfferProps, 'code' | 'iterationNr'>) {
  const apiService = useApiService();
  const { data, loading, error, refetch } = usePrivateOfferBundleData({ code, iterationNr, apiService });
  const t = useTranslations('offers.bundle.not_found');
  const dataTracking = getDataTrackingAttributes(data);

  if (loading) {
    return <OfferLoading />;
  }

  if (error || !data) {
    return <NoResults title={t('title')} description={t('description')} />;
  }

  if (code && !Number.isInteger(iterationNr) && data.recurrenceAssignableByLink) {
    redirect(ROUTES.OFFER_BUNDLE(code, data.iterationNr + 1));
  }

  const isMultipleOffer = data.offers && data.offers.length > 1;

  if (isMultipleOffer) {
    return <MultipleOffer isAuthenticated={true} data={data} refetchCallback={refetch} {...dataTracking} />;
  }

  return <SingleOffer isAuthenticated={true} data={data} refetchCallback={refetch} {...dataTracking} />;
}

function PublicOffer({ code }: Pick<OfferProps, 'code'>) {
  const { data, loading, error } = usePublicOfferBundleData({ code });
  const t = useTranslations('offers.bundle.not_found');
  const dataTracking = getDataTrackingAttributes(data);

  if (loading) {
    return <OfferLoading />;
  }

  if (error || !data) {
    return <NoResults title={t('title')} description={t('description')} />;
  }

  const isMultipleOffer = data.offers && data.offers.length > 1;

  if (isMultipleOffer) {
    return <MultipleOffer isAuthenticated={false} data={data} {...dataTracking} />;
  }

  return <SingleOffer isAuthenticated={false} data={data} {...dataTracking} />;
}

function Offer({ code, iterationNr, className }: OfferProps) {
  const { session } = useSession();
  const isAuthenticated = session.status === 'authenticated';

  if (session.status === 'loading' || session.status === 'uninitialised') {
    return <OfferLoading />;
  }

  return (
    <InnerPageContainer className={className}>
      <div className="pb-10">{isAuthenticated ? <PrivateOffer code={code} iterationNr={iterationNr} /> : <PublicOffer code={code} />}</div>
    </InnerPageContainer>
  );
}

export { Offer };
